/**
 * Datastore React component: owns the current instance's identity and session,
 * and exposes them via DatastoreContext.
 *
 * Most feature code uses `useDatastore()` (or higher-level hooks like
 * `useSessionData`) rather than touching this class directly. The Datastore
 * owns the lifecycle: subscribe to user and persona-preview changes, and reset
 * the session when the active instance changes. Instance data itself is read
 * through the oRPC hooks in `client/data/`, not through this class.
 */

import type { RouterUtils } from '@orpc/react-query';
import type { IsoLocale } from '@repo/shared/locale';
import type { SpaceResult } from '@repo/shared/orpc/procedures/spaces';
import type { UserProfile } from '@repo/shared/orpc/types';
import type { QueryClient } from '@tanstack/react-query';
import { fetchMyRolesAsync } from 'data/me';
import stableJsonStringify from 'json-stable-stringify';
import { getFragment, getIsInSidebar } from 'platform-specific/url';
import React, { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import type { ApiClient } from 'system/orpc';
import { getUser, onUserChanged, type User } from 'util/auth-client';
import { DatastoreContext, IsInSidebarContext, LanguageContext } from 'util/datastoreContext';
import { fireAndForget } from 'util/hooks';
import type { StructureModule } from 'util/structure-module';

import { trackEvent } from './analytics';
import type { AppData } from './appdata';
import { canGoBack, closeWindow, getGlobalParams, goBack, internal_gotoInstance, internal_gotoInstanceScreen } from './navigate';
import { markPoint } from './perf-timer';
import { SharedData } from './shareddata';
import type { TestApiMock } from './test-api';

type ConfigContextValue = {
    config: Record<string, any>;
    screenSet: Record<string, any>;
    activeFeatures: Record<string, boolean>;
};

export const ConfigContext = React.createContext<ConfigContextValue | null | undefined>(undefined);

// TODO: Make this more efficient: Currently every data update updates
// everything.

export type EnabledFeaturesMap = Record<string, boolean>;

// Default caller email for test-mode datastores, mirroring the default
// `personaKey: 'a'`. Hooks that build an identity-scoped request from the
// caller's email (e.g. `useMyChannelKeys`) need a truthy value in tests; a
// suite can override it via `testState.sessionData.email`.
const TEST_SESSION_EMAIL = 'test-admin@example.test';

export type TestState = {
    globals?: any;
    sessionData?: any;
    personaKey?: string | null;
    roles?: string[];
    /**
     * Per-test mocks for typed oRPC procedures. Calls to unmocked procedures
     * throw — see `util/test-api`. Use resource helpers like
     * `mockSpaces([...])` (from `util/test-spaces`) composed via
     * `mockApi(...)`.
     */
    api?: TestApiMock;
    /** Profiles this test declares, answered by `profiles.get` on top of
     * the shared roster. */
    profiles?: UserProfile[];
    space?: Partial<SpaceResult>;
    time?: number;
    isInSidebar?: boolean;
    demoViewportWidth?: number;
    globalParams?: any;
    screenParams?: any;
    language?: IsoLocale;
    pushSubscreen?: (screenKey: string, params: any, options?: { withAnchor?: boolean }) => void;
    replaceSubscreen?: (screenKey: string, params: any) => void;
    gotoInstance?: (params: any) => void;
    goToParentScreen?: () => void;
    gotoInstanceScreen?: (params: any) => void;
    goBack?: () => void;
    closeWindow?: () => void;
    gotoUrl?: (url: string) => void;
    openUrl?: (url: string, target: string | undefined, features: string | undefined) => void;
    urlFragment?: string;
    appData?: AppData;
    config?: any;
    features?: Record<string, boolean> | string[];
};

type DatastoreProps = {
    isAdminUi?: boolean;
    isTestMode: boolean;
    testState?: TestState | null;
    globalParams?: Record<string, any>;
    language: IsoLocale;
    siloKey: string;
    structureKey: string;
    instanceKey: string;
    structureModule: StructureModule;
    activeFeatures: Record<string, boolean>;
    config: Record<string, any>;
    screenSet: Record<string, any>;
    children: React.ReactNode;
    appData: AppData;
    loadingScreen?: React.JSX.Element;
    apiClient: ApiClient;
    queryClient: QueryClient;
    apiQueryUtils: RouterUtils<ApiClient>;
};

type DatastoreState = {
    userLoaded: boolean;
    currentLanguage: IsoLocale;
};

export class Datastore extends React.Component<DatastoreProps, DatastoreState> {
    state = {
        userLoaded: false,
        currentLanguage: this.props.language,
    };
    sessionDataSharedData: SharedData;
    sessionData: Record<string, any> = {};
    userWatchReleaser: null | (() => void) = null;

    constructor(props: DatastoreProps) {
        super(props);

        if (this.props.testState?.appData) {
            this.sessionDataSharedData = this.props.testState.appData.getSharedData(this.props.structureKey, this.props.instanceKey);
        } else if (this.props.appData) {
            this.sessionDataSharedData = this.props.appData.getSharedData(this.props.structureKey, this.props.instanceKey);
        } else {
            this.sessionDataSharedData = new SharedData();
        }
    }

    componentDidMount() {
        this.resetUserSession();
        if (this.props.isTestMode) {
            markPoint(this, 'datastore-loaded');
        } else {
            this.setupUserWatchers();
        }
    }

    componentWillUnmount() {
        this.releaseWatchers();
    }

    releaseWatchers() {
        this.userWatchReleaser?.();
        this.userWatchReleaser = null;
    }

    componentDidUpdate(prevProps: DatastoreProps) {
        if (prevProps.instanceKey !== this.props.instanceKey || prevProps.structureKey !== this.props.structureKey) {
            this.resetUserSession();
            if (!this.props.isTestMode) {
                this.setupUserWatchers();
            }
        }
        if (prevProps.language !== this.props.language) {
            this.setState({ currentLanguage: this.props.language });
        }
    }

    setupUserWatchers() {
        this.releaseWatchers();
        this.userWatchReleaser = onUserChanged((user) => {
            fireAndForget(this.refreshUserDataAsync(user));
        });
    }

    async refreshUserDataAsync(user: User | null | undefined) {
        if (user === undefined) {
            return; // Wait for the user to be loaded before setting user as loaded
        }
        if (user) {
            const roles = await fetchMyRolesAsync(this, user.id);
            this.mergeSessionData({ roles, personaKey: user.id, email: user.email ?? null });
        } else {
            this.mergeSessionData({ roles: [], personaKey: null, email: null });
        }
        this.setState({ userLoaded: true });
    }

    resetSessionData() {
        if (!this.props.isTestMode) {
            const user = getUser();
            this.sessionData = { personaKey: user?.id || null, email: user?.email ?? null };
        } else {
            const { roles = [], sessionData, personaKey = 'a' } = this.props.testState ?? {};

            this.sessionData = { personaKey, email: TEST_SESSION_EMAIL, roles, ...sessionData };
        }
    }

    /**
     * Clear session data and reload the signed-in user, marking the datastore
     * loaded once that settles. Test mode has no user to fetch, so it seeds
     * the fixture's persona and marks loaded immediately.
     */
    resetUserSession() {
        if (!this.props.isTestMode) {
            this.sessionData = {};
            const user = getUser();
            fireAndForget(this.refreshUserDataAsync(user));
        } else {
            const { roles = [], sessionData, personaKey = 'a' } = this.props.testState ?? {};
            this.sessionData = { personaKey, email: TEST_SESSION_EMAIL, roles, ...sessionData };
            this.setState({ userLoaded: true });
        }
    }

    notifySessionDataWatchers() {
        this.sessionDataSharedData.notifyWatchers();
    }

    watchSessionData(watchFunc: () => void) {
        return this.sessionDataSharedData.watch(watchFunc);
    }

    setSessionData(path: string | string[], value: any) {
        this.sessionData = { ...this.sessionData, [pathToName(path)]: value };
        this.notifySessionDataWatchers();
    }

    mergeSessionData(data: Record<string, any>) {
        this.sessionData = { ...this.sessionData, ...data };
        this.notifySessionDataWatchers();
    }

    getSessionData(path: string | string[]): any {
        return this.sessionData[pathToName(path)];
    }

    getPersonaKey(): string | null {
        return this.getSessionData('personaKey');
    }

    /**
     * Re-fetch the current user's roles from the server and update session
     * data. Use this after operations that may change roles (e.g. onboarding in
     * a new silo may grant the owner role).
     */
    async refreshRolesAsync() {
        const user = getUser();
        if (user) {
            this.mergeSessionData({ roles: await fetchMyRolesAsync(this, user.id) });
        }
    }

    getAppData() {
        return this.props.appData;
    }

    /** Returns the API client handed to this Datastore as a prop. */
    getApiClient(): ApiClient {
        return this.props.apiClient;
    }

    /** Returns the query client handed to this Datastore as a prop. */
    getQueryClient(): QueryClient {
        return this.props.queryClient;
    }

    /** Returns the oRPC query utils handed to this Datastore as a prop. */
    getApiQueryUtils(): RouterUtils<ApiClient> {
        return this.props.apiQueryUtils;
    }

    getSiloKey(): string {
        return this.props.siloKey;
    }

    getStructureKey(): string {
        return this.props.structureKey;
    }

    getStructureModule(): StructureModule {
        return this.props.structureModule;
    }

    getActiveFeatures(): Record<string, boolean> {
        return this.props.activeFeatures;
    }

    getInstanceKey(): string {
        return this.props.instanceKey;
    }

    getConfig<T>(): T {
        return this.props.config as T;
    }

    getIsAdminUi() {
        return this.props.isAdminUi ?? false;
    }

    getIsTestMode() {
        return this.props.isTestMode;
    }

    getLanguage() {
        return this.state.currentLanguage;
    }

    getUserLoaded() {
        return this.state.userLoaded;
    }

    getGlobalParams() {
        if (this.props.testState?.globalParams) {
            return this.props.testState?.globalParams;
        } else if (this.props.globalParams) {
            return this.props.globalParams;
        } else {
            return getGlobalParams(window.location.href) ?? {};
        }
    }

    getMyRoles(): string[] {
        const roles = this.getSessionData('roles');
        return roles;
    }

    getIsAdmin(): boolean {
        const roles = this.getMyRoles();
        return roles && roles.length > 0;
    }

    getTime() {
        if (!this.props.isTestMode) {
            return Date.now();
        } else {
            return this.props.testState?.time ?? Date.now();
        }
    }

    getTestScreenParams() {
        return this.props.testState?.screenParams ?? null;
    }

    pushSubscreen(screenKey: string, params: any = {}, { withAnchor = false }: { withAnchor?: boolean } = {}) {
        if (this.props.testState?.pushSubscreen) {
            this.props.testState?.pushSubscreen(screenKey, params, { withAnchor });
        } else {
            internal_gotoInstanceScreen({
                structureKey: this.getStructureKey(),
                instanceKey: this.getInstanceKey(),
                screenKey,
                params,
                withAnchor,
            });
        }
    }

    /**
     * Navigates from a subscreen back to the parent screen of the current
     * instance. Use when you need to exit a nested screen and return to the
     * primary view.
     */
    goToParentScreen() {
        if (this.props.testState?.goToParentScreen) {
            this.props.testState?.goToParentScreen();
        } else {
            this.gotoInstance({ structureKey: this.getStructureKey(), instanceKey: this.getInstanceKey(), params: {} });
        }
    }

    replaceSubscreen(screenKey: string, params: any = {}) {
        if (this.props.testState?.replaceSubscreen) {
            this.props.testState?.replaceSubscreen(screenKey, params);
        } else {
            internal_gotoInstanceScreen({
                structureKey: this.getStructureKey(),
                instanceKey: this.getInstanceKey(),
                screenKey,
                params,
                replace: true,
            });
        }
    }

    gotoInstance({
        structureKey,
        instanceKey = 'one',
        params = {},
        globalParams = {},
        replace = false,
    }: {
        structureKey: string;
        instanceKey?: string;
        params?: any;
        globalParams?: any;
        replace?: boolean;
    }) {
        if (this.props.testState?.gotoInstance) {
            this.props.testState?.gotoInstance({ structureKey, instanceKey, params, globalParams });
        } else {
            internal_gotoInstance({
                structureKey,
                instanceKey,
                params,
                globalParams: { ...this.getGlobalParams(), ...globalParams },
                replace,
            });
        }
    }

    gotoInstanceScreen({
        structureKey,
        instanceKey = 'one',
        screenKey,
        params = {},
        globalParams = {},
        replace = false,
    }: {
        structureKey: string;
        instanceKey?: string;
        screenKey: string;
        params?: any;
        globalParams?: any;
        replace?: boolean;
    }) {
        if (this.props.testState?.gotoInstance) {
            this.props.testState?.gotoInstance({ structureKey, instanceKey, screenKey, params, globalParams });
        } else {
            internal_gotoInstanceScreen({
                structureKey,
                instanceKey,
                screenKey,
                params,
                globalParams: { ...this.getGlobalParams(), ...globalParams },
                replace,
            });
        }
    }

    needsLogin(
        callback: (...params: any[]) => void,
        action?: string,
        gotoAfterAuth?: { structureKey: string; instanceKey: string; screenKey?: string; params: any },
    ): (...params: any[]) => void {
        return (...params) => {
            trackEvent(this, 'login-required', { action });
            const personaKey = this.getPersonaKey();

            if (gotoAfterAuth) {
                this.props.appData?.setGotoAfterAuth(gotoAfterAuth);
            }
            if (personaKey) {
                callback(...params);
                if (gotoAfterAuth) {
                    this.gotoAfterAuth();
                }
            } else {
                this.gotoInstance({ structureKey: 'login', instanceKey: 'one', params: { action } });
            }
        };
    }

    goBack() {
        if (this.props.testState?.goBack) {
            this.props.testState?.goBack();
        } else {
            goBack();
        }
    }

    goBackAfterLogin({ action }: { action?: string } = {}) {
        if (this.props.appData?.getGotoAfterAuth()) {
            this.gotoAfterAuth(true);
        } else if (canGoBack()) {
            this.goBack();
        } else {
            const structureKey = action === 'admin' ? 'admin' : 'account';
            this.gotoInstance({ structureKey, instanceKey: 'one', replace: true });
        }
    }

    gotoAfterAuth(replace = false) {
        const gotoAfterAuth = this.props.appData?.getGotoAfterAuth();
        if (gotoAfterAuth) {
            this.props.appData?.clearGotoAfterAuth();
            if (gotoAfterAuth.screenKey) {
                this.gotoInstanceScreen({ ...gotoAfterAuth, screenKey: gotoAfterAuth.screenKey, replace });
            } else {
                this.gotoInstance({ ...gotoAfterAuth, replace });
            }
        }
    }

    closeWindow() {
        if (this.props.testState?.closeWindow) {
            this.props.testState?.closeWindow();
        } else {
            closeWindow();
        }
    }

    /**
     * Full-document navigation, like its `closeWindow` and `openUrl` siblings:
     * fire-and-forget, returning nothing for callers to await.
     */
    gotoUrl(url: string) {
        if (this.props.testState?.gotoUrl) {
            this.props.testState?.gotoUrl(url);
        } else {
            window.location.href = url;
        }
    }

    openUrl(url: string, target?: string, features?: string) {
        if (this.props.testState?.openUrl) {
            this.props.testState?.openUrl(url, target, features);
        } else {
            window.open(url, target, features);
        }
    }

    getUrlFragment() {
        if (this.props.testState?.urlFragment) {
            return this.props.testState?.urlFragment;
        } else {
            return getFragment();
        }
    }

    render() {
        const content = this.getUserLoaded() ? this.props.children : (this.props.loadingScreen ?? null);

        return (
            <SidebarContextWrapper isInSidebar={this.props.testState?.isInSidebar} isTestMode={this.props.isTestMode}>
                <DatastoreContext.Provider value={this}>
                    <LanguageContext.Provider value={this.state.currentLanguage}>
                        <ConfigProvider datastore={this}>{content}</ConfigProvider>
                    </LanguageContext.Provider>
                </DatastoreContext.Provider>
            </SidebarContextWrapper>
        );
    }
}

// In test mode, provides the in-sidebar flag via context so `useIsInSidebar`
// stays decoupled from the datastore. Production renders no provider, leaving
// `useIsInSidebar` to consult the real environment.
function SidebarContextWrapper({
    isTestMode,
    isInSidebar,
    children,
}: {
    isTestMode?: boolean;
    isInSidebar?: boolean;
    children: React.ReactNode;
}) {
    if (!isTestMode) {
        return children;
    }
    return <IsInSidebarContext.Provider value={isInSidebar ?? true}>{children}</IsInSidebarContext.Provider>;
}

type ConfigProviderProps = {
    datastore: Datastore;
    children: React.ReactNode;
};

/**
 * Puts the `config`/`screenSet`/`activeFeatures` the mount already assembled
 * onto `ConfigContext`.
 *
 * A context read is what lets a consumer several layers down react to a new
 * `activeFeatures` prop even where an ancestor reuses the same `children`
 * element across renders (a toggle-config demo replays actions against a
 * `content` element built once, for instance): React still propagates a
 * changed context value through such a subtree, where it otherwise bails out
 * of re-rendering. `datastore.getActiveFeatures()` stays as the plain-method
 * form for callers outside a render, like analytics.
 */
function ConfigProvider({ datastore, children }: ConfigProviderProps) {
    const { config, screenSet, activeFeatures } = datastore.props;
    const contextValue = useMemo(() => ({ config, screenSet, activeFeatures }), [config, screenSet, activeFeatures]);
    return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
}

export function useDatastore(): Datastore {
    return React.useContext(DatastoreContext) as Datastore;
}

export function useUserLoaded() {
    const datastore = useDatastore();
    return datastore.getUserLoaded();
}

export function useIsInSidebar() {
    return React.useContext(IsInSidebarContext) ?? getIsInSidebar();
}

export function useTrackEvent(eventKey: string, params?: Record<string, any>, skip: boolean = false) {
    const datastore = useDatastore();
    const stableParams = useStableJson(params);
    useEffect(() => {
        if (!skip) {
            trackEvent(datastore, eventKey, stableParams);
        }
    }, [datastore, eventKey, stableParams, skip]);
}

/**
 * Re-renders whenever session data changes, for a component that reads that
 * data indirectly and so has no key to hand `useSessionData` or
 * `useSessionDataValues`.
 */
export function useSessionDataRefresh(): void {
    const datastore = useDatastore();
    const subscribe = useCallback((onChange: () => void) => datastore.watchSessionData(onChange), [datastore]);
    useSyncExternalStore(subscribe, () => datastore.sessionData);
}

export function useSessionData(path: string[] | string) {
    useSessionDataRefresh();
    const datastore = useDatastore();
    return datastore.getSessionData(path);
}

/**
 * The current value of each of several session-data keys, keyed by path.
 *
 * `useSessionData` covers a component that knows its key when it's written.
 * This covers one whose keys are only known at runtime — a table whose
 * filters each declare the key they read — since a hook can't be called once
 * per entry of a list that varies.
 */
export function useSessionDataValues(paths: string[]): Record<string, unknown> {
    useSessionDataRefresh();
    const datastore = useDatastore();
    return Object.fromEntries(paths.map((path) => [path, datastore.getSessionData(path)]));
}

export function usePersonaKey(): string | null {
    return useSessionData('personaKey');
}

/** The authenticated caller's email, or `null` when logged out. */
export function useMyEmail(): string | null {
    return useSessionData('email') ?? null;
}

function pathToName(path: string | string[]) {
    if (typeof path === 'string') {
        return path;
    } else {
        return path.join('/');
    }
}

export function useSiloKey(): string {
    const datastore = useDatastore();
    return datastore.getSiloKey();
}

export function useIsAdminUi(): boolean {
    const datastore = useDatastore();
    return datastore.getIsAdminUi();
}

export function useIsTestMode(): boolean {
    const datastore = useDatastore();
    return datastore.getIsTestMode();
}

export function useInstanceKey(): string {
    const datastore = useDatastore();
    return datastore.getInstanceKey();
}

export function useStructureKey(): string {
    const datastore = useDatastore();
    return datastore.getStructureKey();
}

export function useStructureModule(): StructureModule {
    const datastore = useDatastore();
    return datastore.getStructureModule();
}

export function useGlobalParams(): Record<string, any> {
    const datastore = useDatastore();
    return datastore.getGlobalParams();
}

// useStableCallback allows you to pass a callback to a child component without
// causing it to re-render when the callback changes
export function useStableCallback(callback: (...args: any[]) => void) {
    const ref = useRef<(...args: any[]) => void>(callback);

    useEffect(() => {
        ref.current = callback;
    }, [callback]);

    return useCallback((...args: any[]) => ref.current(...args), []);
}

// useLatestRef lets an effect read the most recent value of something without
// firing when that value changes. Pair with a useEffect whose deps deliberately
// exclude `value` - the ref always holds the latest value at read time.
export function useLatestRef<T>(value: T): React.MutableRefObject<T> {
    const ref = useRef(value);
    useEffect(() => {
        ref.current = value;
    });
    return ref;
}

// useStableJson returns a value whose reference only changes when its content
// changes. Lets effects list an object/array directly in their deps without
// firing on every parent re-render. For arrays of strings, prefer
// useStableStringList.
//
// Uses React's "update ref during render" pattern: the ref holds the last (key,
// value) pair that agreed. When a new render produces a different key, we swap
// the ref; otherwise we return the stored value. This gives the same
// memoization effect as useMemo but without a dep-list the linter can't verify.
// Keys are sorted (via json-stable-stringify) so structurally equal objects
// built in different key orders collapse to the same key.
export function useStableJson<T>(value: T): T {
    // json-stable-stringify returns undefined for values JSON.stringify also
    // can't represent (e.g. a top-level function). Treat undefined as its own
    // equivalence class.
    const key = stableJsonStringify(value) ?? '__undefined__';
    const ref = useRef<{ key: string; value: T }>({ key, value });
    if (ref.current.key !== key) {
        ref.current = { key, value };
    }
    return ref.current.value;
}

// useStableStringList is the string[]-specialized form of useStableJson. Uses a
// length-prefixed join so the key is collision-free regardless of element
// content (without paying for JSON stringification). Use this for id lists, key
// lists, etc., when the input is a prop you don't own. For state you do own,
// wrap setState with `replaceEqualDeep` from `@tanstack/query-core`.
export function useStableStringList(list: string[]): string[] {
    const key = list.length + '|' + list.map((s) => s.length + ':' + s).join('|');
    const ref = useRef<{ key: string; value: string[] }>({ key, value: list });
    if (ref.current.key !== key) {
        ref.current = { key, value: list };
    }
    return ref.current.value;
}
